import type { shopifyAppsDetectionSchema } from "schemas/agentTools/customIntegrations/loaders/shopify";
import type {
  allConnectionStatusesOutputSchema,
  connectionStatusOutputSchema,
  createConnectSessionOutputSchema,
  deleteConnectionOutputSchema,
  discoverIntegrationTargetsOutputSchema,
  filePickerSessionSchema,
  integrationCatalogOutputSchema,
  mentionableObjectsOutputSchema,
  saveConnectionOutputSchema,
  searchObjectsInputSchema,
  searchObjectsOutputSchema,
  updateConnectionInputSchema,
} from "schemas/agentTools/integrationTools";
import type { IntegrationScopeKey } from "schemas/generated/agentIntegration";
import type { z } from "zod";
import type { RequestBlobFn, RequestFn } from "./request";

export function createIntegrationsClient({ request }: { request: RequestFn }) {
  return {
    getCatalog: (): Promise<z.infer<typeof integrationCatalogOutputSchema>> => {
      return request({
        method: "GET",
        path: "/api/v1/agent-integrations/catalog",
      });
    },

    getAllConnectionStatuses: (input: {
      projectId: string;
    }): Promise<z.infer<typeof allConnectionStatusesOutputSchema>> => {
      return request({
        method: "GET",
        path: `/api/v1/projects/${input.projectId}/integrations/connections/statuses`,
      });
    },

    getConnectionStatus: (input: {
      projectId: string;
      integrationKey: string;
    }): Promise<z.infer<typeof connectionStatusOutputSchema>> => {
      return request({
        method: "GET",
        path: `/api/v1/projects/${input.projectId}/integrations/connections/status`,
        query: { integrationKey: input.integrationKey },
      });
    },

    createConnectSession: (input: {
      integrationKey: string;
      connectionConfig: Record<string, string> | null;
    }): Promise<z.infer<typeof createConnectSessionOutputSchema>> => {
      return request({
        method: "POST",
        path: "/api/v1/agent-integrations/connect/session",
        body: input,
      });
    },

    saveConnection: (input: {
      projectId: string;
      integrationKey: string;
      connectionScope: IntegrationScopeKey;
      nangoConnectionId: string;
    }): Promise<z.infer<typeof saveConnectionOutputSchema>> => {
      const { projectId, ...body } = input;
      return request({
        method: "POST",
        path: `/api/v1/projects/${projectId}/integrations/connections/nango`,
        body,
      });
    },

    saveCustomConnection: (input: {
      projectId: string;
      integrationKey: string;
      connectionScope: IntegrationScopeKey;
      customConfig: Record<string, unknown>;
    }): Promise<z.infer<typeof saveConnectionOutputSchema>> => {
      const { projectId, ...body } = input;
      return request({
        method: "POST",
        path: `/api/v1/projects/${projectId}/integrations/connections/custom`,
        body,
      });
    },

    deleteConnection: (input: {
      projectId: string;
      connectionId: string;
    }): Promise<z.infer<typeof deleteConnectionOutputSchema>> => {
      return request({
        method: "DELETE",
        path: `/api/v1/projects/${input.projectId}/integrations/connections/${encodeURIComponent(input.connectionId)}`,
      });
    },

    discoverTargets: (input: {
      projectId: string;
      connectionId: string;
    }): Promise<z.infer<typeof discoverIntegrationTargetsOutputSchema>> => {
      return request({
        method: "GET",
        path: `/api/v1/projects/${input.projectId}/integrations/connections/${encodeURIComponent(input.connectionId)}/targets`,
      });
    },

    updateConnection: (
      input: {
        projectId: string;
        connectionId: string;
      } & z.infer<typeof updateConnectionInputSchema>,
    ): Promise<z.infer<typeof connectionStatusOutputSchema>> => {
      const { projectId, connectionId, ...body } = input;
      return request({
        method: "PUT",
        path: `/api/v1/projects/${projectId}/integrations/connections/${encodeURIComponent(connectionId)}`,
        body,
      });
    },

    createFilePickerSession: (input: {
      projectId: string;
    }): Promise<z.infer<typeof filePickerSessionSchema>> => {
      return request({
        method: "POST",
        path: `/api/v1/projects/${input.projectId}/integrations/file-pickers/dropbox/session`,
      });
    },

    findShopifyApps: (input: {
      projectId: string;
    }): Promise<z.infer<typeof shopifyAppsDetectionSchema>> => {
      return request({
        method: "GET",
        path: `/api/v1/projects/${input.projectId}/integrations/shopify/apps`,
      });
    },

    searchObjects: (
      input: { projectId: string } & z.infer<typeof searchObjectsInputSchema>,
    ): Promise<z.infer<typeof searchObjectsOutputSchema>> => {
      const { projectId, ...body } = input;
      return request({
        method: "POST",
        path: `/api/v1/projects/${projectId}/integrations/search-objects`,
        body,
      });
    },

    getMentionableObjects: (input: {
      projectId: string;
    }): Promise<z.infer<typeof mentionableObjectsOutputSchema>> => {
      return request({
        method: "GET",
        path: `/api/v1/projects/${input.projectId}/integrations/mentionable-objects`,
      });
    },
  };
}

export type IntegrationsClient = ReturnType<typeof createIntegrationsClient>;

/** Byte downloads, only on browser clients, which have a blob path. */
export function createIntegrationsBlobClient({
  requestBlob,
}: {
  requestBlob: RequestBlobFn;
}) {
  return {
    /** A file from the person's own Drive; Google Docs, Sheets and Slides come back exported. */
    getGoogleDriveFileContent: (input: {
      projectId: string;
      fileId: string;
    }): Promise<Blob> => {
      return requestBlob({
        method: "GET",
        path: `/api/v1/projects/${input.projectId}/integrations/google-drive/files/${encodeURIComponent(input.fileId)}/content`,
      });
    },
  };
}
